// client/src/components/company/CompanyAnalytics.js

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Calendar, DollarSign, Filter } from 'lucide-react';
import { useResponsive } from '../../hooks/useResponsive';
import { formatInterestRate } from '../utils/formatUtils';
import { useAuth } from '../../context/AuthContext';

const CompanyAnalytics = () => {
  const { t, i18n } = useTranslation();
  const { isMobile } = useResponsive();
  const { user } = useAuth();
  const isPaying = user?.organization?.isPaying;
  
  // Chart states
  const [selectedRequestType, setSelectedRequestType] = useState('allocation');
  const [selectedMaturity, setSelectedMaturity] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [selectedBanks, setSelectedBanks] = useState([]);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [uniqueBanks, setUniqueBanks] = useState([]);
  const [uniqueCurrencies, setUniqueCurrencies] = useState([]);

  // Date filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Common states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Get available maturities for selected currency
  const getAvailableMaturities = useCallback(() => {
    // Filter maturities that have at least one data point in the selected currency
    return analyticsData.filter(maturityGroup => {
      return maturityGroup.dataPoints.some(point => point.currency === selectedCurrency);
    });
  }, [analyticsData, selectedCurrency]);

  // Get filtered banks based on current maturity and currency
  const getFilteredBanks = useCallback(() => {
    const maturityData = analyticsData.find(m => m.maturity === selectedMaturity);
    if (!maturityData) return [];

    const banksInView = new Set();
    
    maturityData.dataPoints.forEach(point => {
      // Filter by currency (no more 'all' option)
      if (point.currency !== selectedCurrency) {
        return;
      }
      
      Object.keys(point.banks).forEach(bank => {
        banksInView.add(bank);
      });
    });

    return Array.from(banksInView).sort();
  }, [analyticsData, selectedMaturity, selectedCurrency]);

  const fetchAnalyticsData = useCallback(async (requestType) => {
    try {
      setLoading(true);
      setSelectedMaturity('');

      const response = await axios.get('/api/analytics/bid-trends-by-maturity', {
        params: requestType && requestType !== 'all' ? { requestType } : {}
      });

      if (response.data.success && response.data.data.length > 0) {
        setAnalyticsData(response.data.data);
        
        // Extract all unique banks from the data
        const banksSet = new Set();
        const currenciesSet = new Set();
        
        response.data.data.forEach(maturityGroup => {
          maturityGroup.dataPoints.forEach(point => {
            // Add currency
            if (point.currency) {
              currenciesSet.add(point.currency);
            }
            // Add banks
            Object.keys(point.banks).forEach(bank => banksSet.add(bank));
          });
        });
        
        const banksList = Array.from(banksSet).sort();
        setUniqueBanks(banksList);
        setSelectedBanks(banksList); // Select all banks by default
        
        const currencies = Array.from(currenciesSet).sort();
        setUniqueCurrencies(currencies);
        setSelectedCurrency(prev => {
          if (prev && currencies.includes(prev)) return prev;
          return currencies.includes('EUR') ? 'EUR' : (currencies[0] || '');
        });
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(t('common.errors.fetchData', { defaultValue: 'Failed to fetch analytics data' }));
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAnalyticsData(selectedRequestType);
  }, [fetchAnalyticsData, selectedRequestType]);

  // Auto-select all banks when data changes or currency filter changes
  useEffect(() => {
    if (uniqueBanks.length > 0) {
      const filteredBanks = getFilteredBanks();
      setSelectedBanks(filteredBanks.map(bank => bank));
    }
  }, [selectedMaturity, selectedCurrency, uniqueBanks, analyticsData, getFilteredBanks]);

  // When currency changes, select first available maturity for that currency
  useEffect(() => {
    const availableMaturities = getAvailableMaturities();
    if (availableMaturities.length > 0) {
      // Check if current maturity is still available, otherwise select first available
      const currentMaturityAvailable = availableMaturities.some(m => m.maturity === selectedMaturity);
      if (!currentMaturityAvailable) {
        setSelectedMaturity('');
      }
    }
  }, [selectedCurrency, analyticsData, getAvailableMaturities, selectedMaturity]);

  const formatDuration = (duration) => {
    const currentLang = i18n.language;
    
    if (duration === 'overnight') return t('requests.overnight', { defaultValue: 'Overnight' });
    if (duration === '7d') return currentLang === 'it' ? '7 giorni' : '7 days';
    if (duration === '14d') return currentLang === 'it' ? '14 giorni' : '14 days';
    if (duration === '21d') return currentLang === 'it' ? '21 giorni' : '21 days';
    if (duration === 'lt15') return currentLang === 'it' ? '< 15gg' : '< 15d';
    if (duration === '15_29') return currentLang === 'it' ? '15-29gg' : '15-29d';
    if (duration === '30_90') return currentLang === 'it' ? '30-90gg' : '30-90d';
    if (duration === 'gt90') return currentLang === 'it' ? '> 90gg' : '> 90d';
    
    const months = parseInt(duration);
    if (currentLang === 'it') {
      return months === 1 ? '1 mese' : `${months} mesi`;
    }
    return months === 1 ? '1 month' : `${months} months`;
  };

  const formatDate = (dateString) => {
    const currentLang = i18n.language;
    const date = new Date(dateString);
    return date.toLocaleDateString(currentLang === 'it' ? 'it-IT' : 'en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  const formatDateTime = (dateString) => {
    const currentLang = i18n.language;
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString(currentLang === 'it' ? 'it-IT' : 'en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
    const timeStr = date.toLocaleTimeString(currentLang === 'it' ? 'it-IT' : 'en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    return `${dateStr} ${timeStr}`;
  };

  const formatRate = (rate) => {
    return `${rate.toFixed(2)}%`;
  };

  const formatRateWithType = (value, bankName, dataPoint) => {
  if (!dataPoint) return formatRate(value);
  
  const metadata = dataPoint[`${bankName}_metadata`];
  if (metadata && metadata.rateType === 'floating' && metadata.baseRate) {
    // Usa la funzione formatInterestRate da formatUtils che ha già tutti i mapping
    return formatInterestRate(metadata.originalRate, 'floating', metadata.baseRate);
  }
  
  return formatRate(value);
};

  const formatCurrency = (currency) => {
    return currency; // Simply return the currency code (EUR, USD, etc.)
  };

  // Get colors for banks (consistent across all charts)
  const getBankColor = (index) => {
    const colors = [
      '#0066CC', // Blu intenso
      '#DC3545', // Rosso
      '#28A745', // Verde
      '#FFA500', // Arancione
      '#6F42C1', // Viola
      '#FF5722', // Arancione bruciato
      '#E83E8C', // Rosa acceso
      '#FFC107', // Giallo oro
      '#17A2B8', // Cyan
      '#6C757D', // Grigio
      '#FF6B6B', // Rosso chiaro
      '#795548'  // Marrone
    ];
    return colors[index % colors.length];
  };

  // Prepare chart data for selected maturity with filters
  const getChartData = () => {
    const maturityData = analyticsData.find(m => m.maturity === selectedMaturity);
    if (!maturityData) return [];

    return maturityData.dataPoints
      .filter(point => {
        // Filter by currency (no more 'all' option)
        if (point.currency !== selectedCurrency) return false;
        
        // Filter by date range if dates are set
        if (startDate || endDate) {
          const pointDate = new Date(point.date);
          
          if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            if (pointDate < start) return false;
          }
          
          if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            if (pointDate > end) return false;
          }
        }
        
        return true;
      })
      .map((point, index) => {
        const dataPoint = {
          date: formatDate(point.date),
          uniqueKey: `${point.date}_${index}`, // Add unique key for multiple requests on same date
          originalDate: point.date, // Keep original date for reference
          currency: point.currency
      };

        // Calculate average for selected currency
        const currencyAvg = point.averageByCurrency?.[selectedCurrency];
        if (currencyAvg !== undefined) {
          dataPoint.average = currencyAvg;
        }

        // Add each selected bank's rate
        Object.entries(point.banks).forEach(([bankName, data]) => {
          // Only include if bank is selected
          if (selectedBanks.includes(bankName)) {
            dataPoint[bankName] = data.rate;
            // Store metadata for variable rates
            dataPoint[`${bankName}_metadata`] = {
              rateType: data.rateType,
              baseRate: data.baseRate,
              originalRate: data.originalRate 
            };
          }
        });

        return dataPoint;
      });
  };

  const chartData = getChartData();

  const allRates = chartData.flatMap(point =>
    [...uniqueBanks.map(bank => point[bank]), point.average].filter(v => v !== undefined && typeof v === 'number')
  );
  const minRate = allRates.length > 0 ? Math.min(...allRates) : 0;
  const maxRate = allRates.length > 0 ? Math.max(...allRates) : 5;
  const yMin = Math.round(Math.max(0, minRate - 0.5) * 2) / 2;
  const yMax = Math.round((maxRate + 0.5) * 2) / 2;
  const yTicks = [];
  for (let t = yMin; t <= yMax + 0.001; t = Math.round((t + 0.5) * 2) / 2) yTicks.push(t);

  // Handle bank selection toggle
  const toggleBankSelection = (bank) => {
    setSelectedBanks(prev => {
      if (prev.includes(bank)) {
        return prev.filter(b => b !== bank);
      } else {
        return [...prev, bank];
      }
    });
  };

  // Handle select all / deselect all
  const toggleAllBanks = () => {
    const filteredBanks = getFilteredBanks();
    if (selectedBanks.length === filteredBanks.length) {
      setSelectedBanks([]);
    } else {
      setSelectedBanks(filteredBanks);
    }
  };

  if (loading) {
    return (
      <div className="company-analytics company-dashboard">
        <div className="loading">{t('common.loading', { defaultValue: 'Loading...' })}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="company-analytics company-dashboard">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (analyticsData.length === 0) {
    return (
      <div className="company-analytics company-dashboard">
        <div className="card">
          <p>{t('analytics.noData', { defaultValue: 'No analytics data available yet. Complete some requests to see trends.' })}</p>
        </div>
      </div>
    );
  }

  const filteredBanks = getFilteredBanks();

  return (
    <div className="company-analytics company-dashboard">
      {/* MAIN CHART: Bid Trends by Maturity with Filters */}
      {analyticsData.length > 0 && (
        <div className="card">
          <div className="card-title">
            <TrendingUp className="card-title-icon" size={20} />
            <span>{t('analytics.bidTrendsByMaturity', { defaultValue: 'Interest Rate Evolution by Maturity' })}</span>
          </div>

          {/* Filters Section - Box 1: Maturity and Currency */}
          <div style={{
            backgroundColor: '#ffffff',
            border: '2px solid #e3f2fd',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
              gap: '16px'
            }}>
              {/* Request Type Selector */}
              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="request-type-select" style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#424242'
                }}>
                  <Filter size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  {t('analytics.selectType', { defaultValue: 'Type' })}
                </label>
                <select
                  id="request-type-select"
                  className="form-select-custom"
                  value={selectedRequestType}
                  onChange={(e) => setSelectedRequestType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid #bbdefb',
                    borderRadius: '6px',
                    backgroundColor: '#f5f9ff',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <option value="allocation">{t('analytics.typeDeposit', { defaultValue: 'Deposits' })}</option>
                  {isPaying && (
                    <option value="hot_money">{t('analytics.typeHotMoney', { defaultValue: 'Hot Money' })}</option>
                  )}
                </select>
              </div>

              {/* Maturity Selector */}
              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="maturity-select" style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#424242'
                }}>
                  <Calendar size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  {t('analytics.selectMaturity', { defaultValue: 'Select Maturity' })}
                </label>
                <select
                  id="maturity-select"
                  className="form-select-custom"
                  value={selectedMaturity}
                  onChange={(e) => setSelectedMaturity(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid #bbdefb',
                    borderRadius: '6px',
                    backgroundColor: '#f5f9ff',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <option value="">
                    {t('analytics.selectMaturityPlaceholder', { defaultValue: '-- Select Maturity --' })}
                  </option>
                  {getAvailableMaturities().map(item => (
                    <option key={item.maturity} value={item.maturity}>
                      {formatDuration(item.maturity)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Currency Filter */}
              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="currency-select" style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#424242'
                }}>
                  <DollarSign size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  {t('analytics.selectCurrency', { defaultValue: 'Select Currency' })}
                </label>
                <select
                  id="currency-select"
                  className="form-select-custom"
                  value={selectedCurrency}
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid #bbdefb',
                    borderRadius: '6px',
                    backgroundColor: '#f5f9ff',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {uniqueCurrencies.map(currency => (
                    <option key={currency} value={currency}>
                      {formatCurrency(currency)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Date Range Filters */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
              gap: '16px',
              marginTop: '16px',
              paddingTop: '16px',
              borderTop: '1px solid #e3f2fd'
            }}>
              {/* Start Date */}
              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="start-date" style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#424242'
                }}>
                  <Calendar size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  {t('analytics.startDate', { defaultValue: 'Start Date' })}
                </label>
                <input
                  type="date"
                  id="start-date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid #bbdefb',
                    borderRadius: '6px',
                    backgroundColor: '#f5f9ff',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                />
              </div>

              {/* End Date */}
              <div className="form-group" style={{ margin: 0 }}>
                <label htmlFor="end-date" style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#424242'
                }}>
                  <Calendar size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  {t('analytics.endDate', { defaultValue: 'End Date' })}
                </label>
                <input
                  type="date"
                  id="end-date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid #bbdefb',
                    borderRadius: '6px',
                    backgroundColor: '#f5f9ff',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                />
              </div>
            </div>

            {/* Clear Date Filters Button */}
            {(startDate || endDate) && (
              <div style={{ marginTop: '12px', textAlign: 'right' }}>
                <button
                  onClick={() => {
                    setStartDate('');
                    setEndDate('');
                  }}
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    backgroundColor: '#f5f5f5',
                    color: '#424242',
                    border: '1px solid #e0e0e0',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#eeeeee';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#f5f5f5';
                  }}
                >
                  {t('analytics.clearDateFilters', { defaultValue: 'Clear Date Filters' })}
                </button>
              </div>
            )}
          </div>

          {/* Filters Section - Box 2: Bank Filter */}
          <div style={{
            backgroundColor: '#ffffff',
            border: '2px solid #e3f2fd',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '16px',
                fontWeight: '600',
                color: '#1976d2',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Filter size={18} />
                {t('analytics.filterBanks', { defaultValue: 'Filter Banks' })}
                <span style={{ 
                  fontSize: '13px', 
                  fontWeight: '400',
                  color: '#757575',
                  backgroundColor: '#e3f2fd',
                  padding: '2px 10px',
                  borderRadius: '12px'
                }}>
                  {selectedBanks.length} / {filteredBanks.length} {t('analytics.selected', { defaultValue: 'selected' })}
                </span>
              </h3>
              
              <button
                onClick={toggleAllBanks}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '500',
                  backgroundColor: selectedBanks.length === filteredBanks.length ? '#f5f5f5' : '#1976d2',
                  color: selectedBanks.length === filteredBanks.length ? '#424242' : '#ffffff',
                  border: selectedBanks.length === filteredBanks.length ? '1px solid #e0e0e0' : 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => {
                  if (selectedBanks.length === filteredBanks.length) {
                    e.target.style.backgroundColor = '#eeeeee';
                  } else {
                    e.target.style.backgroundColor = '#1565c0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedBanks.length === filteredBanks.length) {
                    e.target.style.backgroundColor = '#f5f5f5';
                  } else {
                    e.target.style.backgroundColor = '#1976d2';
                  }
                }}
              >
                {selectedBanks.length === filteredBanks.length 
                  ? t('analytics.deselectAll', { defaultValue: 'Deselect All' })
                  : t('analytics.selectAll', { defaultValue: 'Select All' })
                }
              </button>
            </div>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))', 
              gap: '12px',
              maxHeight: '240px',
              overflowY: 'auto',
              padding: '16px',
              backgroundColor: '#e3f2fd',
              borderRadius: '8px',
              border: '1px solid #bbdefb'
            }}>
              {filteredBanks.length === 0 ? (
                <div style={{
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  padding: '20px',
                  color: '#757575',
                  fontSize: '14px'
                }}>
                  {t('analytics.noBanksAvailable', { defaultValue: 'No banks available for the selected filters' })}
                </div>
              ) : (
                filteredBanks.map((bank, index) => (
                                    <label 
                    key={bank}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      cursor: 'pointer',
                      padding: '10px 12px',
                      fontSize: '14px',
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f9ff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedBanks.includes(bank)}
                      onChange={() => toggleBankSelection(bank)}
                      style={{ 
                        marginRight: '10px',
                        cursor: 'pointer',
                        width: '18px',
                        height: '18px',
                        accentColor: '#1976d2'
                      }}
                    />
                    <span style={{ 
                      display: 'inline-block',
                      width: '14px',
                      height: '14px',
                      backgroundColor: getBankColor(uniqueBanks.indexOf(bank)),
                      marginRight: '10px',
                      borderRadius: '3px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                    }}></span>
                    <span style={{ 
                      fontWeight: '400',
                      color: '#424242',
                      flex: 1
                    }}>
                      {bank}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Chart */}
          <div style={{ width: '100%', height: isMobile ? 300 : 400, marginTop: '20px' }}>
            <ResponsiveContainer>
              <LineChart 
                data={chartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis 
                  dataKey="uniqueKey"
                  tickFormatter={(value) => {
                    // Extract date from uniqueKey (format: "date_index")
                    const point = chartData.find(p => p.uniqueKey === value);
                    return point ? point.date : value;
                  }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  angle={isMobile ? -45 : 0}
                  textAnchor={isMobile ? "end" : "middle"}
                  height={isMobile ? 60 : 30}
                />
                <YAxis
                  domain={[yMin, yMax]}
                  ticks={yTicks}
                  tickFormatter={(value) => `${value.toFixed(1)}%`}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  label={{ 
                    value: t('analytics.interestRate', { defaultValue: 'Interest Rate (%)' }), 
                    angle: -90, 
                    position: 'insideLeft',
                    style: { fontSize: isMobile ? 10 : 12 }
                  }}
                />
                <Tooltip 
                  formatter={(value, name, props) => formatRateWithType(value, name, props.payload)}
                  labelFormatter={(label) => {
                    // Find the data point with this uniqueKey
                    const point = chartData.find(p => p.uniqueKey === label);
                    return point ? formatDateTime(point.originalDate) : label;
                  }}
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: isMobile ? 11 : 13
                  }}
                />
                <Legend 
                  wrapperStyle={{ fontSize: isMobile ? 11 : 13 }}
                  iconType="line"
                />

                {/* Average line - rendered first but with zIndex to stay below banks */}
                <Line 
                  type="monotone" 
                  dataKey="average" 
                  stroke="#000000"
                  strokeWidth={2}
                  name={`\u200B\u200B\u200B${t('analytics.average', { defaultValue: 'Average' })}`}
                  dot={{ r: 6 }}
                  zIndex={1}
                />

                {/* Bank lines - only for selected banks */}
                {uniqueBanks.map((bank, index) => {
                  // Only render if bank is selected and has data in current view
                  if (!selectedBanks.includes(bank)) return null;
                  
                  const hasData = chartData.some(point => point[bank] !== undefined);
                  if (!hasData) return null;

                  return (
                    <Line 
                      key={bank}
                      type="monotone" 
                      dataKey={bank} 
                      stroke={getBankColor(index)}
                      strokeWidth={0.7}
                      name={bank}
                      dot={{ r: 7, fill: getBankColor(index) }}
                      connectNulls
                      activeDot={{ r: 8 }}
                      zIndex={10}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Legend explanation */}
          <div style={{ 
            marginTop: '20px', 
            padding: '15px', 
            backgroundColor: '#f5f5f5', 
            borderRadius: '8px',
            fontSize: isMobile ? '12px' : '14px'
          }}>
            <p style={{ margin: 0, color: '#757575' }}>
              <strong>{t('analytics.chartExplanation', { defaultValue: 'Chart shows:' })}</strong>
              {' '}
              {t('analytics.chartDescription', { 
                defaultValue: 'Evolution of interest rates over time for the selected maturity. Each point represents a rate bid. The black line shows the average rate. The variable rates were calculated using the numerical value of the reference rate.' 
              })}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyAnalytics;